import { Label, Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { Price } from 'src/api/endpoints/cloudConnect.types';
import {
  useChangeEnterpriseSubscriptionBillingInterval,
  useEnterpriseSubscription,
} from 'src/api/queries/enterpriseSubscription';
import { Button, Divider, Form, Modal, NotificationBox } from 'src/components';
import { Link2 } from 'src/components/Link2';
import { NumberInput } from 'src/components/NumberInput';
import { PriceConnect } from 'src/components/PriceConnect';
import { formatDate } from 'src/utils/formatDate';

export const BILLING_ROUTE_PATH = '/_auth/enterprise/_with-org/_layout/billing';

const billingRouteApi = getRouteApi(BILLING_ROUTE_PATH);

type FormValues = {
  seats: number;
};

export function ChangePlanAnnualModal() {
  const search = billingRouteApi.useSearch();
  const navigate = billingRouteApi.useNavigate();
  const subscription = useEnterpriseSubscription();
  const changeBillingInterval =
    useChangeEnterpriseSubscriptionBillingInterval();
  const form = useForm<FormValues>({
    defaultValues: {
      seats: subscription.data?.planCurrent.prices.monthly.quantity || 1,
    },
  });

  const currentAmountOfSeats =
    subscription.data?.planCurrent.prices.monthly.quantity || 1;

  const newSeatsAmount = form.watch('seats');

  const newPrice = useMemo(() => {
    if (!subscription.data) return null;
    const annualPlan = subscription.data.planOptions.find(
      (plan) => plan.interval === 'annual'
    );
    if (!annualPlan) return null;
    const newQuantity = newSeatsAmount || currentAmountOfSeats;
    return {
      ...annualPlan.prices.annual,
      quantity: newQuantity,
      totalAmount: annualPlan.prices.annual.unitAmount * newQuantity,
    } satisfies Price;
  }, [subscription.data, newSeatsAmount]);

  const annualPlan = useMemo(() => {
    if (!subscription.data) return null;
    return subscription.data.planOptions.find(
      (plan) => plan.interval === 'annual'
    );
  }, [subscription.data]);

  if (!annualPlan) return null;

  return (
    <Modal.Content className="flex flex-col gap-24 text-left">
      <Modal.Title asChild>
        <Typography as="h2" variant="heading-md" className="text-center">
          Switch to annual plan
        </Typography>
      </Modal.Title>

      <Modal.Description asChild>
        <Typography variant="body-md">
          This plan requires a 1-year commitment for a fixed number of seats at
          a discounted rate of{' '}
          <PriceConnect price={annualPlan.prices.monthly} itemLabel="seat" />
        </Typography>
      </Modal.Description>

      <Divider />

      {search.switchToAnnualPlan === 'select-seats' && (
        <Form
          {...form}
          onSubmit={() => {
            return navigate({
              to: '.',
              search: (prev) => ({
                ...prev,
                switchToAnnualPlan: 'confirm',
              }),
            });
          }}
          className="flex flex-col gap-16"
        >
          <div>
            <Typography variant="body-md" className="mb-8 font-semibold">
              Set your annual seat commitment
            </Typography>
            <Typography variant="body-md">
              You can increase your seat count at any time. You may only reduce
              the seat count at the time of renewal. No refunds will be issued
              for the remaining period after cancellation.
            </Typography>
          </div>

          <div>
            <Controller
              control={form.control}
              name="seats"
              rules={{
                required: 'Please enter the number of seats.',
                min: {
                  value: currentAmountOfSeats,
                  message: `You must commit to at least ${currentAmountOfSeats} seats.`,
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <Label htmlFor={field.name} className="mb-8 inline-block">
                    Your current seats: {currentAmountOfSeats}
                  </Label>

                  <NumberInput
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    id={field.name}
                    ref={field.ref}
                    disabled={field.disabled}
                    min={currentAmountOfSeats}
                    data-cy="annual-plan-seat-input"
                    hasError={!!fieldState.error}
                  />

                  {fieldState.error && (
                    <div className="relative">
                      <Typography
                        data-cy="annual-plan-form-error-message"
                        variant="body-sm"
                        className="absolute text-feedback-red-500"
                      >
                        {fieldState.error.message}
                      </Typography>
                    </div>
                  )}
                </>
              )}
            />
          </div>

          <Divider className="mt-20" />

          <div className="mt-24 flex w-full flex-col justify-center gap-16 ls:flex-row">
            <Modal.Close asChild>
              <Button
                variant="secondary"
                mobileFullWidth
                analytics={{
                  text: 'Cancel',
                  location:
                    ComponentLocations.ENTERPRISE.MODAL_SWITCH_TO_ANNUAL,
                  action: 'cancel switch to annual plan',
                }}
              >
                Cancel
              </Button>
            </Modal.Close>

            <Button
              type="submit"
              data-cy="confirm-annual-plan-button"
              variant="primary"
              mobileFullWidth
              analytics={{
                text: 'Continue',
                location: ComponentLocations.ENTERPRISE.MODAL_SWITCH_TO_ANNUAL,
                action: 'continue to confirm switch to annual plan',
              }}
            >
              Continue
            </Button>
          </div>
        </Form>
      )}

      {search.switchToAnnualPlan === 'confirm' && (
        <div className="flex flex-col gap-16">
          <table className="max-w-sm gap-4">
            <tbody>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Seats committed:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    {newSeatsAmount}
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Price per seat:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    <PriceConnect
                      price={annualPlan.prices.annual}
                      variant="unit"
                    />
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Total price:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    <PriceConnect price={newPrice} variant="total" />
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Starting date:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    {formatDate(subscription.data?.periodEnd)}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>

          <Divider className="mt-12" />

          <div className="mt-24 flex w-full flex-col justify-center gap-16 ls:flex-row">
            <Link2
              componentLocation={
                ComponentLocations.ENTERPRISE.MODAL_SWITCH_TO_ANNUAL
              }
              as="button"
              to="."
              search={{ switchToAnnualPlan: 'select-seats' }}
              variant="secondary"
              className="w-full ls:w-fit"
            >
              Back
            </Link2>

            <Button
              variant="primary"
              size="medium"
              loading={changeBillingInterval.isPending}
              onClick={() =>
                changeBillingInterval.mutate(
                  {
                    interval: 'annual',
                    seats: newSeatsAmount,
                  },
                  {
                    onSuccess: () => {
                      return navigate({
                        to: '.',
                        search: (prev) => ({
                          ...prev,
                          switchToAnnualPlan: undefined,
                        }),
                      });
                    },
                  }
                )
              }
              data-cy="confirm-annual-plan-change-button"
              mobileFullWidth
              analytics={{
                text: 'Confirm',
                location: ComponentLocations.ENTERPRISE.MODAL_SWITCH_TO_ANNUAL,
                action: 'confirm switch to annual plan',
              }}
            >
              Confirm
            </Button>
          </div>

          <NotificationBox error={changeBillingInterval.error} />
        </div>
      )}
    </Modal.Content>
  );
}
